
/**
 * Module dependencies.
 */

import { QueryObserverBaseResult, useQuery } from 'react-query';
import { fecthShowroomModule } from './fetch-showroom-module';
import { useMemo } from 'react';
import { useRouter } from 'next/router';

/**
 * `Props` type.
 */

type Props<P> = QueryObserverBaseResult<P, unknown>;

/**
 * `Options` type.
 */

type Options<P> = {
  initialData?: P,
  staleTime?: number
};

/**
 * Action type.
 */

const actionType = ({ locale }): string => {
  return `${locale}-showroom-module`;
};

/**
 * Default stale time.
 *
 * The showroom module is near static and is read by the footer on every page,
 * so without this the site would hit the api again on every window focus.
 */

const defaultStaleTime = 30 * 60 * 1000;

/**
 * `useShowroomModule` hook.
 */

function useShowroomModule<P>(options?: Options<P>): Props<P> {
  const { locale } = useRouter();
  const variables = useMemo(() => ({ locale }), [locale]);
  const result = useQuery([actionType(variables), variables], () => {
    return fecthShowroomModule(variables);
  }, {
    initialData: options?.initialData,
    staleTime: options?.staleTime ?? defaultStaleTime
  });

  return result;
}

/**
 * Export `useShowroomModule` hook.
 */

export default useShowroomModule;
